import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, of, timer } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

import { StorageService } from 'ngx-webstorage-service';

import { BroadcastChannelActions } from '@common/model/broadcast-channel-actions';
import { AppStorageService } from '@common/services/app-storage.service';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';

@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private readonly bundleRe = /main-\w+\.js/;
  private readonly document = inject(DOCUMENT);
  private readonly http = inject(HttpClient);
  private readonly broadcastChannelService = inject(BroadcastChannelService);
  private readonly storageService = inject<StorageService>(AppStorageService);

  private readonly currentMainSrc = this.document
    .querySelector<HTMLScriptElement>('script[src*="main-"]')
    ?.getAttribute('src') ?? null;

  private readonly updateAvailable = new BehaviorSubject(false);
  readonly isUpdateAvailable$ = this.updateAvailable.asObservable();

  private readonly pollLockKey = 'fbo_version_poll_lock';
  private readonly pollLockTimeoutMs = 6 * 60 * 1000; // outlasts the 5-min interval

  private tryAcquirePollLock(): string | null {
    const existing = this.storageService.get(this.pollLockKey) as string | null;
    if (existing != null) {
      const ts = parseInt(existing.split(':')[0], 10);
      if (!Number.isNaN(ts) && Date.now() - ts < this.pollLockTimeoutMs) {
        return null; // another tab holds the lock
      }
    }
    const owner = Math.random().toString(36).slice(2);
    const value = `${Date.now()}:${owner}`;
    this.storageService.set(this.pollLockKey, value);
    return (this.storageService.get(this.pollLockKey) as string | null) === value ? owner : null;
  }

  private releasePollLock(owner: string): void {
    if ((this.storageService.get(this.pollLockKey) as string | null)?.endsWith(`:${owner}`)) {
      this.storageService.remove(this.pollLockKey);
    }
  }

  start(): void {
    // Only poll in builds where Angular has hashed the main bundle (i.e. not ng serve)
    if (!this.currentMainSrc?.match(this.bundleRe)) {
      return;
    }

    timer(0, 5 * 60 * 1000)
      .pipe(
        switchMap(() => {
          const owner = this.tryAcquirePollLock();
          if (!owner) {
            return EMPTY; // another tab is handling this tick
          }
          return this.http.get(`index.html?_=${Date.now()}`, { responseType: 'text' }).pipe(
            catchError(() => of(null as string | null)),
            tap(() => this.releasePollLock(owner))
          );
        }),
        filter((html): html is string => html !== null),
        filter(html => {
          const bundleSrc = html.match(this.bundleRe)?.[0] ?? null;
          return bundleSrc !== null && bundleSrc !== this.currentMainSrc;
        })
      )
      .subscribe(() => this.updateAvailable.next(true));
  }

  reload(): void {
    this.broadcastChannelService.sendMessage({ action: BroadcastChannelActions.channelTabsRefresh });
    window.location.reload();
  }
}
